// Client-side fetching of the per-(tab, page) listing JSON that
// `bun run build` emits (see `newsPageData` in vite.config.ts). Page 1 of each
// tab comes from the prerendered loader data; later pages are fetched here.
import { queryOptions } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import type { NewsPage, NewsType } from './types'

async function fetchNewsPage(type: NewsType, page: number): Promise<NewsPage> {
  const res = await fetch(
    `/${import.meta.env.NEWS_DATA_DIR}/${type}/${page}.json`,
  )
  if (!res.ok) throw new Error(String(res.status))
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- JSON our own build emitted from `NewsPage` (vite.config.ts); a runtime schema would ship zod to the browser
  return (await res.json()) as NewsPage
}

/**
 * The JSON is immutable per deploy (its URL carries a content hash), so it is
 * never stale; a missing page (404) is final, so no retries.
 */
export const newsPageQuery = (type: NewsType, page: number) =>
  queryOptions({
    queryKey: ['news-page', type, page],
    queryFn: () => fetchNewsPage(type, page),
    staleTime: Infinity,
    // The old cache kept every page for the session.
    gcTime: Infinity,
    retry: false,
  })

/**
 * Fetches a page's JSON into the cache (a no-op if it is already there), for
 * hover and idle prefetch. Resolves to the page so the caller can warm its
 * images; never rejects.
 */
export function prefetchNewsPage(
  queryClient: QueryClient,
  type: NewsType,
  page: number,
): Promise<NewsPage | undefined> {
  return queryClient
    .ensureQueryData(newsPageQuery(type, page))
    .catch(() => undefined)
}
